import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { toObservable } from '@angular/core/rxjs-interop';
import { finalize, Observable, switchMap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AlertsComponent,
  AlertsService,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { HttpErrorService } from '@web/common';
import { AdminSubscriptionDataSourceService } from '../../services/admin-subscription-data-source.service';
import { Guid } from '@web/utils';
import { AdminSubscriptionShippingAddressFormComponent } from '../admin-subscription-shipping-address-form/admin-subscription-shipping-address-form.component';
import { AdminSubscriptionShippingAddressForm } from '../../models/admin-subscription-shipping-address-form';

@Component({
  selector: 'app-admin-subscription-shipping-address-edit',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AlertsComponent,
    SpinnerFilledComponent,
    AdminSubscriptionShippingAddressFormComponent,
  ],
  templateUrl: './admin-subscription-shipping-address-edit.component.html',
  styleUrl: './admin-subscription-shipping-address-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class AdminSubscriptionShippingAddressEditComponent {
  public readonly id = input.required<Guid>();
  public readonly submitEvent = output<void>();

  protected readonly pending = signal(false);
  protected readonly id$ = this.idSource();
  protected readonly data$ = this.dataSource();

  /* prettier-ignore */
  private readonly subscriptionDataSource = inject(AdminSubscriptionDataSourceService);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);

  public handleSubmitClickEvent(
    data: AdminSubscriptionShippingAddressForm,
  ): void {
    this.markAsPending();

    this.subscriptionDataSource
      .updateShippingAddressByFormData({
        id: this.id(),
        data,
      })
      .pipe(finalize(() => this.markAsResolved()))
      .subscribe({
        next: () => this.submitEvent.emit(),
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  private markAsPending(): void {
    this.pending.set(true);
  }

  private markAsResolved(): void {
    this.pending.set(false);
  }

  private idSource(): Observable<Guid> {
    return toObservable(this.id);
  }

  private dataSource(): Observable<AdminSubscriptionShippingAddressForm> {
    return this.id$.pipe(
      switchMap((id) =>
        this.subscriptionDataSource.fetchShippingAddressFormById(id),
      ),
    );
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    const messages = this.httpError.extract(response).messages();

    this.alerts.showMultiple((builder) =>
      messages.map((message) => builder.buildFailure(message)),
    );
  }
}
